import {useQuery} from "react-query";
import axios from "axios";
import {ADataGridFilter, PagedSearchParams} from "@atiautomacao/ati-ui-library";
import { LocalDateTimeFormatISO } from "../../../Utils/DateFormatPatternUtils";
import { addDays, isValid } from "date-fns";
import { mergeAndDeduplicateFilters } from "../../../Utils/FilterMergeUtils";

const apiUrl = "api/audit/alarm/search";

export const appendPowerStationIds = (params: URLSearchParams, powerStationIds?: number[] | null) => {
    if (!powerStationIds || powerStationIds.length === 0) return;
    powerStationIds.forEach((id) => params.append("powerStationIds", String(id)));
};

const searchAlarms = async (pagedSearchParams: PagedSearchParams, powerStationIds?: number[] | null) => {
    try {
        const params = new URLSearchParams(pagedSearchParams.toURLSearchParams());
        appendPowerStationIds(params, powerStationIds);
        const response = await axios
            .get(apiUrl, {
                params,
                timeout: 30000,
            })
            .then((res) => res.data);

        return response ?? 0;
    } catch (error : any) {
        console.error("Error fetching data:", error);
        return error.response?.data?.message || "An error occurred while fetching data.";
    }
};

/**
 * Fim do período efetivamente consultado. Chamado a cada fetch (inclusive os do auto-refresh), não entra na
 * chave da query: assim o refresh traz alarmes novos sem trocar a chave e sem reativar o skeleton.
 *
 * Enquanto o usuário não edita "Data final", o fim acompanha o "agora" (nunca abaixo do fim inicial);
 * depois de editado, vale exatamente o que foi informado.
 *
 * @param startDatetime início do período
 * @param endDatetime fim informado pelo usuário (ou o padrão da tela, o instante em que ela abriu)
 * @param isEndPristine true enquanto o usuário não editou o campo "Data final"
 * @param now instante do fetch
 * @param maxRangeDays limite opcional do período: o fim que acompanha o "agora" não passa de início + limite
 */
export const resolveQueryEnd = (
    startDatetime: Date,
    endDatetime: Date,
    isEndPristine: boolean,
    now: Date,
    maxRangeDays?: number
): Date => {
    if (!isEndPristine) return endDatetime;

    const liveEnd = now.getTime() > endDatetime.getTime() ? now : endDatetime;
    if (maxRangeDays === undefined) return liveEnd;

    const limit = addDays(startDatetime, maxRangeDays);
    return liveEnd.getTime() > limit.getTime() ? limit : liveEnd;
};

/**
 * Parâmetros da busca/exportação: filtros de coluna + filtros avançados + período.
 * Os avançados são unidos aos de coluna com mergeAndDeduplicateFilters, como na tela de Alarmes: valores da mesma
 * chave viram um único filtro separado por ~ (o backend os combina por OU). Sem isso, chaves repetidas
 * (ex.: severityName.contains da coluna e do avançado) chegariam duplicadas e o backend manteria só a primeira.
 */
export const buildAuditSearchParams = (
    pagedSearchParams: PagedSearchParams,
    advancedFilters: ADataGridFilter[],
    startDatetime: Date,
    endDatetime: Date
) => {
    const columnFilters: ADataGridFilter[] = JSON.parse(JSON.stringify(pagedSearchParams.filters));
    const params = new PagedSearchParams(
        mergeAndDeduplicateFilters(columnFilters, advancedFilters, columnFilters),
        pagedSearchParams.page,
        pagedSearchParams.size
    );
    params.filters.push({name: "dateTime", condition: "starts-at", value: LocalDateTimeFormatISO(startDatetime), sort: "none"});
    params.filters.push({name: "dateTime", condition: "ends-at", value: LocalDateTimeFormatISO(endDatetime), sort: "none"});
    return params;
};

export const AuditAlarmsQuery = (
    pagedSearchParams: PagedSearchParams,
    startDatetime: Date,
    endDatetime: Date,
    powerStationIds?: number[] | null,
    isPowerStationSelectionReady: boolean = true,
    advancedFilters: ADataGridFilter[] = [],
    isEndPristine: boolean = false,
    maxRangeDays?: number,
    refetchInterval: number | false = false
) => {
    const isValidRange = isValid(startDatetime) && isValid(endDatetime);

    return useQuery({
        queryKey: [
            "data",
            isValidRange ? buildAuditSearchParams(pagedSearchParams, advancedFilters, startDatetime, endDatetime).toURLSearchParams().toString() : "invalid-range",
            powerStationIds,
        ],
        queryFn: () => searchAlarms(
            buildAuditSearchParams(pagedSearchParams, advancedFilters, startDatetime, resolveQueryEnd(startDatetime, endDatetime, isEndPristine, new Date(), maxRangeDays)),
            powerStationIds
        ),
        enabled: isValidRange && isPowerStationSelectionReady,
        refetchInterval,
        refetchIntervalInBackground: false,
    });
};